/**
 * Dashboard Selector Component
 * Allows users to switch between different dashboard views
 * Now with role-based access control
 */

import { Check, ChevronDown } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useFilter } from '@/contexts/filter-context';
import { useAuth } from '@/hooks/use-auth';
import {
  canAccessDashboard,
  type DashboardOption,
  getDashboardOptions,
  isManagerOrAbove,
} from '@/utils/dashboard-options';

export function DashboardSelector() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { isEveryoneFilter } = useFilter();
  const [open, setOpen] = useState(false);

  // Get dashboard options based on user role AND filter selection
  // Collections Dashboard only shows when "Everyone" is selected
  const dashboardOptions = getDashboardOptions(user.role, isEveryoneFilter);

  // Determine current dashboard based on route
  const currentDashboard =
    dashboardOptions.find((option) => {
      if (option.route === '/') {
        return location.pathname === '/';
      }
      return location.pathname.startsWith(option.route);
    }) || dashboardOptions[0];

  // Redirect if user tries to access a dashboard they don't have permission for
  useEffect(() => {
    if (currentDashboard && !canAccessDashboard(currentDashboard.id, user.role)) {
      console.warn(
        `[DashboardSelector] User ${user.name} (${user.role}) attempted to access restricted dashboard: ${currentDashboard.id}`,
      );
      navigate('/');
    }
  }, [currentDashboard, user.role, user.name, navigate]);

  const handleSelect = (option: DashboardOption) => {
    // Double-check access before navigating
    if (!canAccessDashboard(option.id, user.role)) {
      console.warn(`[DashboardSelector] Access denied for ${user.name} (${user.role}) to dashboard: ${option.id}`);
      return;
    }

    navigate(option.route);
    setOpen(false);
  };

  // Debug logging
  console.log('=== Dashboard Dropdown Debug ===');
  console.log('[DashboardSelector] User:', user.name);
  console.log('[DashboardSelector] User role:', user.role);
  console.log('[DashboardSelector] Is manager or above:', isManagerOrAbove(user.role));
  console.log('[DashboardSelector] Filter: "Everyone" selected:', isEveryoneFilter);
  console.log('[DashboardSelector] Dashboard options:', dashboardOptions);
  console.log('[DashboardSelector] Number of options:', dashboardOptions.length);
  console.log(
    '[DashboardSelector] Option IDs:',
    dashboardOptions.map((o) => o.id),
  );
  console.log(
    '[DashboardSelector] Option titles:',
    dashboardOptions.map((o) => o.title),
  );
  console.log('================================');

  // Separate main dashboard from specialized dashboards
  const mainDashboard = dashboardOptions.find((opt) => opt.id === 'main');
  const specializedDashboards = dashboardOptions.filter((opt) => opt.id !== 'main');

  // Check if user is a manager to show the "Specialized Dashboards" header
  const showSpecializedHeader =
    isManagerOrAbove(user.role) && specializedDashboards.some((opt) => opt.isSpecialized && opt.requiredRoles);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="flex items-center justify-between gap-2 py-1.5 px-3 min-w-[240px]">
          <div className="flex items-center gap-2">
            <currentDashboard.icon className="h-4 w-4" />
            <span className="font-medium">{currentDashboard.title}</span>
          </div>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[400px]">
        {/* Main Dashboard - Always first */}
        {mainDashboard && (
          <DropdownMenuItem
            className="flex items-start gap-3 p-3 cursor-pointer"
            onClick={() => handleSelect(mainDashboard)}
          >
            <div className="mt-0.5">
              <mainDashboard.icon className="h-4 w-4" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <span className="font-medium">{mainDashboard.title}</span>
                {currentDashboard.id === mainDashboard.id && <Check className="h-4 w-4 text-primary" />}
              </div>
              <p className="text-sm text-muted-foreground mt-0.5">{mainDashboard.description}</p>
            </div>
          </DropdownMenuItem>
        )}

        {/* Specialized Dashboards Section */}
        {specializedDashboards.length > 0 && (
          <>
            <DropdownMenuSeparator />
            {/* Show "Specialized Dashboards" header only for managers with restricted dashboards */}
            {showSpecializedHeader && (
              <>
                <DropdownMenuLabel className="text-xs text-muted-foreground font-normal px-3 py-2">
                  Specialized Dashboards
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
              </>
            )}
            {/* Render all specialized dashboards */}
            {specializedDashboards.map((option) => {
              const IconComponent = option.icon;
              return (
                <DropdownMenuItem
                  key={option.id}
                  className="flex items-start gap-3 p-3 cursor-pointer"
                  onClick={() => handleSelect(option)}
                >
                  <div className="mt-0.5">
                    <IconComponent className="h-4 w-4" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{option.title}</span>
                      {currentDashboard.id === option.id && <Check className="h-4 w-4 text-primary" />}
                    </div>
                    <p className="text-sm text-muted-foreground mt-0.5">{option.description}</p>
                  </div>
                </DropdownMenuItem>
              );
            })}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export type { DashboardOption } from '@/utils/dashboard-options';
// Re-export utilities for convenience
export { canAccessDashboard, getDashboardOptions, isManagerOrAbove } from '@/utils/dashboard-options';
